import React, { useMemo, useState } from "react";
import { api, extractError } from "../../lib/api";
import { SectionCard } from "../Ui";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

const MOVEMENTS = [
  { value: "PACKING_TO_STACK", label: "Packing floor to stack" },
  { value: "DIRECT_PACKING_TO_TRUCK", label: "Direct packing to truck" },
  { value: "STACK_TO_TRUCK", label: "Stack to truck" },
  { value: "STACK_TO_REPROCESS_CUT", label: "Stack to reprocess cut" },
];

function bags(n) {
  return Number(n || 0).toLocaleString("en-IN");
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function WarehouseStackRegister({ data, contractors, onReload }) {
  const invariant = data?.invariant || {};
  const stacks = data?.stacks || [];
  const grouped = useMemo(() => {
    const rows = data?.stacks || [];
    const map = new Map();
    rows.forEach((stack) => {
      const key = stack.godown_name || "Godown";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(stack);
    });
    return [...map.entries()];
  }, [data?.stacks]);

  const [stackForm, setStackForm] = useState({ godown_name: "Main godown", stack_number: "", capacity_bags: "", item_id: "" });
  const [move, setMove] = useState({
    work_date: todayIso(),
    movement_type: "PACKING_TO_STACK",
    contractor_id: "",
    bags_count: "",
    pack_size_kg: "50",
    stack_id: "",
    production_batch_id: "",
    item_id: "",
  });
  const [busy, setBusy] = useState(false);

  const needsStack = move.movement_type !== "DIRECT_PACKING_TO_TRUCK";
  const needsStock = move.movement_type === "STACK_TO_TRUCK" || move.movement_type === "DIRECT_PACKING_TO_TRUCK" || move.movement_type === "STACK_TO_REPROCESS_CUT";

  const createStack = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/warehouse/stacks", {
        godown_name: stackForm.godown_name,
        stack_number: stackForm.stack_number,
        capacity_bags: Number(stackForm.capacity_bags || 0),
        item_id: stackForm.item_id ? Number(stackForm.item_id) : null,
      });
      toast.success("Stack added to the godown register.");
      setStackForm((f) => ({ ...f, stack_number: "", capacity_bags: "" }));
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const logMovement = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/warehouse/movements", {
        work_date: move.work_date,
        movement_type: move.movement_type,
        contractor_id: Number(move.contractor_id),
        bags_count: Number(move.bags_count),
        pack_size_kg: Number(move.pack_size_kg),
        stack_id: needsStack && move.stack_id ? Number(move.stack_id) : null,
        production_batch_id: move.production_batch_id ? Number(move.production_batch_id) : null,
        item_id: move.item_id ? Number(move.item_id) : null,
      });
      toast.success("Movement posted. Stack and finished stock follow the approved bag count.");
      setMove((m) => ({ ...m, bags_count: "" }));
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground" data-testid="stock-invariant-banner">
        Total packed: {bags(invariant.total_packed_bags)} bags
        {" | "}
        Available: {bags(invariant.available_bags)} bags
        {" | "}
        In stacks: {bags(invariant.in_stacks_bags)} bags
        {" | "}
        Floor / transit: {bags(invariant.floor_transit_bags)} bags
      </div>

      <SectionCard title="Godown stacks" subtitle="Available finished bags equal approved packed bags minus dispatch and reprocess cuts. Stacks plus floor hold that total.">
        {grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground">No stacks are on the register yet.</p>
        ) : (
          <div className="space-y-5">
            {grouped.map(([godown, rows]) => (
              <div key={godown}>
                <h4 className="text-sm font-semibold text-foreground mb-3">{godown}</h4>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {rows.map((stack) => {
                    const pct = stack.utilization_pct == null ? 0 : Math.min(100, stack.utilization_pct);
                    return (
                      <article key={stack.id} className="rounded-lg border border-border bg-card p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-foreground">Stack {stack.stack_number}</p>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{stack.status}</span>
                        </div>
                        <p className="text-sm text-foreground">{bags(stack.current_bags_count)} bags{stack.capacity_bags ? ` / ${bags(stack.capacity_bags)}` : ""}</p>
                        <Progress value={pct} />
                        <p className="text-xs text-muted-foreground">
                          {stack.item_name || "No variety"}{stack.lot_number ? ` · Lot ${stack.lot_number}` : ""}
                        </p>
                      </article>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Add a stack">
          <form className="grid gap-3" onSubmit={createStack}>
            <div className="space-y-1.5">
              <Label>Godown</Label>
              <Input value={stackForm.godown_name} onChange={(e) => setStackForm((f) => ({ ...f, godown_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Stack number</Label>
              <Input value={stackForm.stack_number} onChange={(e) => setStackForm((f) => ({ ...f, stack_number: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Capacity (bags)</Label>
              <Input type="number" min="0" value={stackForm.capacity_bags} onChange={(e) => setStackForm((f) => ({ ...f, capacity_bags: e.target.value }))} />
            </div>
            <Button type="submit" disabled={busy}>Save stack</Button>
          </form>
        </SectionCard>

        <SectionCard title="Log a bag movement" subtitle="Stacking, truck loading, and reprocess cuts bill the loading contractor and move the stack count.">
          <form className="grid gap-3" onSubmit={logMovement}>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={move.work_date} onChange={(e) => setMove((m) => ({ ...m, work_date: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Movement</Label>
              <Select value={move.movement_type} onValueChange={(v) => setMove((m) => ({ ...m, movement_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOVEMENTS.map((row) => (
                    <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Loading contractor</Label>
              <Select value={move.contractor_id || undefined} onValueChange={(v) => setMove((m) => ({ ...m, contractor_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent>
                  {contractors.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {needsStack && (
              <div className="space-y-1.5">
                <Label>Stack</Label>
                <Select value={move.stack_id || undefined} onValueChange={(v) => setMove((m) => ({ ...m, stack_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select stack" /></SelectTrigger>
                  <SelectContent>
                    {stacks.map((stack) => (
                      <SelectItem key={stack.id} value={String(stack.id)}>
                        {stack.godown_name} · {stack.stack_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Bags</Label>
                <Input type="number" min="1" value={move.bags_count} onChange={(e) => setMove((m) => ({ ...m, bags_count: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label>Bag size (kg)</Label>
                <Input type="number" min="1" step="0.01" value={move.pack_size_kg} onChange={(e) => setMove((m) => ({ ...m, pack_size_kg: e.target.value }))} required />
              </div>
            </div>
            {needsStock && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Batch</Label>
                  <Select value={move.production_batch_id || undefined} onValueChange={(v) => setMove((m) => ({ ...m, production_batch_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Batch" /></SelectTrigger>
                    <SelectContent>
                      {(data?.batches || []).map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>{b.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Variety</Label>
                  <Select value={move.item_id || undefined} onValueChange={(v) => setMove((m) => ({ ...m, item_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Item" /></SelectTrigger>
                    <SelectContent>
                      {(data?.items || []).map((item) => (
                        <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <Button type="submit" disabled={busy}>Post movement</Button>
          </form>
        </SectionCard>
      </div>
    </div>
  );
}
